import { ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { useYardStores } from '@yard/AppProvider'
import {
  OTHER_REST_VALUE,
  paletteColorFor,
} from '@yard/utils/color-scheme-palette'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import {
  COLOR_SCHEME_MAX_SELECTIONS,
  COLOR_SCHEMES,
  ColorScheme,
  ColorSchemeRow,
  resolveSchemeDisplayName,
  YardViewStore,
} from '../../stores/yard.view-store'

interface Props {
  viewStore: YardViewStore
}

const VALUE_LIST_MAX_HEIGHT = 320

export const YardColourBySection = observer(({ viewStore }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const { companyStore } = useYardStores()

  const schemeLabels: Record<ColorScheme, string> = {
    portOfDischarge: t('colourByPortOfDischarge', 'Port of Discharge'),
    outboundCarrierType: t('colourByOutboundCarrier', 'Outbound Carrier'),
    inboundCarrierType: t('colourByInboundCarrier', 'Inbound Carrier'),
    operator: t('colourByOperator', 'Operator'),
  }

  const activeScheme = viewStore.colorScheme
  const selectedValues = viewStore.selectedValuesForActiveScheme
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues])

  // Visible rows: top-N named values plus a Rest row when the yard has more
  // distinct values than the top-N can show.
  const topRows = viewStore.topRowsForActiveScheme
  const topRowValueSet = useMemo(() => new Set(topRows.map(r => r.value)), [topRows])
  // Carry-overs: real values the planner still has selected that fall outside
  // the visible top-N (either no longer present at all, or pushed down by
  // other values gaining containers). Pinned at top so they remain toggleable.
  const carryOverRows = useMemo<ColorSchemeRow[]>(() => {
    if (!activeScheme) return []
    const presentCounts = new Map(
      viewStore.presentValuesByScheme[activeScheme].map(r => [r.value, r.count]),
    )
    return selectedValues
      .filter(v => v !== OTHER_REST_VALUE && !topRowValueSet.has(v))
      .map(value => ({ value, count: presentCounts.get(value) ?? 0 }))
  }, [activeScheme, selectedValues, topRowValueSet, viewStore.presentValuesByScheme])
  const valueRows = useMemo<ColorSchemeRow[]>(
    () => [...carryOverRows, ...topRows],
    [carryOverRows, topRows],
  )

  // For the operator scheme the picker value is a stringified operatorId;
  // resolve it to a company name everywhere the user sees a row. The Rest
  // sentinel always renders as the localised "Rest" label.
  const labelFor = (row: ColorSchemeRow) => {
    if (row.isRest) return t('colourByRest', 'Rest')
    return activeScheme
      ? resolveSchemeDisplayName(activeScheme, row.value, companyStore)
      : row.value
  }

  const capReached = selectedValues.length >= COLOR_SCHEME_MAX_SELECTIONS
  const showEmptyState = activeScheme !== undefined && valueRows.length === 0

  // Toggle-all state. "All selected" = every visible row is currently ticked.
  const selectableValues = useMemo(() => valueRows.map(r => r.value), [valueRows])
  const allSelectableChecked =
    selectableValues.length > 0 && selectableValues.every(v => selectedSet.has(v))
  const someSelectableChecked =
    !allSelectableChecked && selectableValues.some(v => selectedSet.has(v))

  const handleSchemeClick = (scheme: ColorScheme) => () => {
    viewStore.setColorScheme(scheme)
  }

  const handleToggleAll = () => {
    if (!activeScheme) return
    if (allSelectableChecked) {
      viewStore.clearSelectedValuesForScheme(activeScheme)
    } else {
      viewStore.setSelectedSchemeValues(activeScheme, selectableValues)
    }
  }

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      sx={{
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { boxShadow: 'none' },
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: theme.customSpacing.s,
      }}
    >
      <AccordionSummary sx={{ m: 0, p: 0 }} expandIcon={<ExpandMore />}>
        <Typography variant='subtitle2'>{t('colourByTitle', 'Colour by')}</Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ m: 0, p: 0, pb: theme.customSpacing.s }}>
        <Stack direction='column' gap={theme.customSpacing.xs}>
          <RadioGroup value={activeScheme ?? ''}>
            {COLOR_SCHEMES.map(scheme => (
              <FormControlLabel
                key={scheme}
                value={scheme}
                label={schemeLabels[scheme]}
                control={
                  <Radio
                    checked={activeScheme === scheme}
                    onClick={handleSchemeClick(scheme)}
                  />
                }
              />
            ))}
          </RadioGroup>

          {activeScheme !== undefined && (
            <Stack gap={theme.customSpacing.xxs}>
              {showEmptyState ? (
                <Typography variant='body2' color='text.secondary'>
                  {t('colourByEmpty', 'No {attr} values in yard', {
                    attr: schemeLabels[activeScheme],
                  })}
                </Typography>
              ) : (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allSelectableChecked}
                        indeterminate={someSelectableChecked}
                        onChange={handleToggleAll}
                      />
                    }
                    label={
                      <Typography variant='body2'>
                        {allSelectableChecked
                          ? t('colourByDeselectAll', 'Deselect all')
                          : t('colourBySelectAll', 'Select all')}
                      </Typography>
                    }
                  />

                  <Box
                    sx={{
                      maxHeight: VALUE_LIST_MAX_HEIGHT,
                      overflowY: 'auto',
                      // Reserve a touch of room on the right so the scrollbar
                      // doesn't sit on top of row content.
                      pr: theme.customSpacing.xxs,
                    }}
                  >
                    {valueRows.map(row => {
                      const { value, count } = row
                      const isChecked = selectedSet.has(value)
                      const disabled = !isChecked && capReached
                      const displayLabel = labelFor(row)
                      return (
                        <Stack
                          key={value}
                          direction='row'
                          alignItems='center'
                          justifyContent='space-between'
                          gap={theme.customSpacing.xs}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isChecked}
                                disabled={disabled}
                                onChange={() => viewStore.toggleSchemeValue(activeScheme, value)}
                              />
                            }
                            label={
                              <Stack direction='row' alignItems='center' gap={theme.customSpacing.xs}>
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    flexShrink: 0,
                                    // Resolve on the raw value (not the label)
                                    // to match the colour used by other surfaces;
                                    // the registry is keyed on the raw value too.
                                    backgroundColor: paletteColorFor(activeScheme, value),
                                    border: `1px solid ${theme.palette.divider}`,
                                  }}
                                />
                                <Typography variant='body2' noWrap>
                                  {displayLabel}
                                </Typography>
                              </Stack>
                            }
                          />
                          <Typography variant='body2' color='text.secondary'>
                            {count}
                          </Typography>
                        </Stack>
                      )
                    })}
                  </Box>

                  {capReached && (
                    <Typography variant='caption' color='text.secondary'>
                      {t('colourByMaxSelections', 'Maximum of {max} selections reached', {
                        max: COLOR_SCHEME_MAX_SELECTIONS,
                      })}
                    </Typography>
                  )}
                </>
              )}
            </Stack>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
})
